import React, { useState, useEffect, useCallback } from 'react';
import { X, Upload, FileText } from 'lucide-react';
import {
  addVehicle,
  updateVehicle,
  getVehicleById,
  getAccountDetails,
  saveAccountDetailsByVehicle,
} from '../../api/vehicalsManagementApi';
import CustomSelect from '../shared/CustomSelect';
import { toast } from '../shared/Toast';
import { VEHICLE_TYPES, VEHICLE_SEATING_DEFAULTS } from '../../constants/vehicleConstants';

const FileUpload = ({ label, accept = 'image/*,application/pdf,.pdf', onChange, value, preview }) => {
  const displayUrl = typeof preview === 'string' ? preview : (typeof value === 'string' ? value : null);
  const isPdf = Boolean(
    (value && (value.type === 'application/pdf' || value.name?.toLowerCase().endsWith('.pdf'))) ||
    (displayUrl && (displayUrl.toLowerCase().includes('.pdf') || displayUrl.toLowerCase().includes('/raw/')))
  );

  const displayName = value instanceof File ? value.name : (displayUrl ? (isPdf ? 'Current PDF Document' : 'Current Image') : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', width: '100%' }}>
      <label style={{
        position: 'absolute', top: '-8px', left: '12px', backgroundColor: 'white',
        padding: '0 4px', fontSize: '11px', color: '#666', fontWeight: 500, zIndex: 1
      }}>
        {label}
      </label>
      <div style={{
        padding: '8px 12px', border: '1px solid #C4C4C4', borderRadius: '4px',
        backgroundColor: 'transparent', width: '100%', boxSizing: 'border-box',
        display: 'flex', alignItems: 'center', gap: '8px',
        minHeight: '44px'
      }}>
        {value || preview ? (
          isPdf ? (
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '6px', maxWidth: '140px', overflow: 'hidden', cursor: displayUrl ? 'pointer' : 'default' }}
              onClick={() => displayUrl && window.open(displayUrl, '_blank')}
              title={displayUrl ? 'Click to view PDF in new tab' : (value?.name || 'PDF Document')}
            >
              <FileText size={20} color="#EF4444" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: '12px', color: displayUrl ? '#DC2626' : '#374151', fontWeight: displayUrl ? 500 : 400, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                {displayName}
              </span>
            </div>
          ) : (
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '6px', maxWidth: '140px', overflow: 'hidden', cursor: displayUrl ? 'pointer' : 'default' }}
              onClick={() => displayUrl && window.open(displayUrl, '_blank')}
              title={displayUrl ? 'Click to view image in new tab' : (value?.name || 'Uploaded Image')}
            >
              {displayUrl ? (
                <img src={displayUrl} alt={label} style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
              ) : (
                <FileText size={18} color="#20B967" style={{ flexShrink: 0 }} />
              )}
              <span style={{ fontSize: '12px', color: '#374151', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                {displayName}
              </span>
            </div>
          )
        ) : (
          <span style={{ fontSize: '13px', color: '#9CA3AF' }}>Choose file</span>
        )}
        <input type="file" accept={accept} onChange={onChange} style={{ display: 'none' }} id={`file-${label}`} />
        <label htmlFor={`file-${label}`} style={{ marginLeft: 'auto', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', color: '#20B967', fontSize: '13px', fontWeight: 500, flexShrink: 0 }}>
          <Upload size={14} /> {value || preview ? 'Change' : 'Upload'}
        </label>
      </div>
    </div>
  );
};

const FormInput = ({ label, placeholder, width = '100%', value, onChange, maxLength }) => (
  <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', width }}>
    <label style={{
      position: 'absolute', top: '-8px', left: '12px', backgroundColor: 'white',
      padding: '0 4px', fontSize: '11px', color: '#666', fontWeight: 500, zIndex: 1
    }}>
      {label}
    </label>
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={e => onChange && onChange(e.target.value)}
      maxLength={maxLength}
      className="form-input-placeholder"
      style={{
        padding: '12px 14px', border: '1px solid #C4C4C4', borderRadius: '4px',
        fontSize: '14px', color: '#333', outline: 'none', backgroundColor: 'transparent',
        width: '100%', boxSizing: 'border-box'
      }}
    />
  </div>
);

const SelectInput = ({ label, options, width = '100%', value, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', width }}>
    <label style={{
      position: 'absolute', top: '-8px', left: '12px', backgroundColor: 'white',
      padding: '0 6px', fontSize: '11px', color: '#6B7280', fontWeight: 500, zIndex: 10,
      borderRadius: '2px'
    }}>
      {label}
    </label>
    <CustomSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder={`Select ${label}`}
      width="100%"
      buttonStyle={{
        padding: '11px 14px',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        fontSize: '14px',
      }}
    />
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#333', margin: '24px 0 16px', display: 'flex', alignItems: 'center' }}>
    {children}
  </h3>
);

const INITIAL_FORM_STATE = {
  registrationNumber: '',
  ownerName: '',
  contactNumber: '',
  address: '',
  vehicleBrand: '',
  vehicleNumber: '',
  vehicleType: '',
  model: '',
  seatingCapacity: '',
  ratePerKm: '',
  apw: 'No',
  apo: 'No',
  accountNumber: '',
  bankName: '',
  bankCategory: 'APW',
  ifscCode: '',
  branch: '',
  driverName: '',
  driverAddress: '',
  driverContact: '',
  aadhar: '',
  panNumber: '',
  license: '',
  status: 'Active',
  // File uploads
  panCardImage: null,
  aadharFrontImage: null,
  aadharBackImage: null,
  licenseImage: null,
  licenseBackImage: null
};

const INITIAL_PREVIEWS_STATE = {
  panCardImage: null,
  aadharFrontImage: null,
  aadharBackImage: null,
  licenseImage: null,
  licenseBackImage: null
};

const getDocUrl = (doc) => {
  if (!doc) return null;
  if (typeof doc === 'string') return doc;
  if (typeof doc === 'object' && doc.url) return doc.url;
  return null;
};

const AddVehicleModal = ({ isOpen, onClose, onSave, vehicle }) => {
  const isEditMode = !!vehicle;

  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [previews, setPreviews] = useState(INITIAL_PREVIEWS_STATE);
  const [loading, setLoading] = useState(false);

  const populateFormData = useCallback((v, accountData = null) => {
    if (!v) return;

    // Resolve Account Details (from separate fetch, nested accountDetails, or vehicle fields)
    const acc = accountData || v.accountDetails || v.account_details || {};
    const fallbackAcc = v.accountDetails || v.account_details || {};
    const accNo = acc.accountNumber || fallbackAcc.accountNumber || v.accountNumber || v.ownerAccountNumber || v.owner_account_number || '';
    const bName = acc.bankName || fallbackAcc.bankName || v.bankName || v.bank_name || '';
    const bCat = acc.bankCategory || fallbackAcc.bankCategory || v.bankCategory || v.bank_category || 'APW';
    const ifsc = acc.ifscCode || fallbackAcc.ifscCode || v.ifscCode || v.ifsc_code || '';
    const br = acc.branch || fallbackAcc.branch || v.branch || '';

    // Resolve Primary Driver Details (from drivers array, nested driver, or vehicle fields)
    const drv = (v.drivers && v.drivers.length > 0 ? v.drivers[0] : null) || v.driver || {};
    const dName = drv.driverName || drv.name || v.driverName || v.driver_name || '';
    const dAddress = drv.address || v.driverAddress || v.driver_address || '';
    const dContact = drv.contactNumber || drv.phone || v.driverContact || v.driver_contact || '';
    const aadharNo = drv.aadharNumber || drv.aadhar || v.aadharNumber || v.aadhar || '';
    const panNo = drv.panNumber || drv.pan || v.panNumber || '';
    const licNo = drv.licenseNumber || drv.license || v.licenseNumber || v.license || '';

    // Document URLs
    const panUrl = getDocUrl(drv.panImage) || getDocUrl(drv.panCardImage) || getDocUrl(v.panCardImage) || getDocUrl(v.panImage);
    const aadharFrontUrl = getDocUrl(drv.aadharFrontImage) || getDocUrl(drv.aadharFront) || getDocUrl(v.aadharFrontImage) || getDocUrl(v.aadharFront);
    const aadharBackUrl = getDocUrl(drv.aadharBackImage) || getDocUrl(drv.aadharBack) || getDocUrl(v.aadharBackImage) || getDocUrl(v.aadharBack);
    const licenseFrontUrl = getDocUrl(drv.licenseImage) || getDocUrl(drv.licenseFront) || getDocUrl(v.licenseImage) || getDocUrl(v.licenseFront);
    const licenseBackUrl = getDocUrl(drv.licenseBackImage) || getDocUrl(drv.licenseBack) || getDocUrl(v.licenseBackImage) || getDocUrl(v.licenseBack);

    setForm({
      registrationNumber: v.registrationNumber || v.registration_number || '',
      ownerName: v.ownerName || v.owner_name || '',
      contactNumber: v.ownerContactNumber || v.owner_contact_number || v.contactNumber || v.contact_number || '',
      address: v.address || v.ownerAddress || v.owner_address || '',
      vehicleBrand: v.vehicleBrand || v.vehicle_brand || '',
      vehicleNumber: v.vehicleNumber || v.vehicle_number || '',
      vehicleType: v.vehicleType || v.vehicle_type || '',
      model: v.model || '',
      seatingCapacity: v.seatingCapacity !== undefined && v.seatingCapacity !== null ? String(v.seatingCapacity) : (v.seating_capacity !== undefined ? String(v.seating_capacity) : ''),
      ratePerKm: v.ratePerKm !== undefined && v.ratePerKm !== null ? String(v.ratePerKm) : (v.rate_per_km !== undefined ? String(v.rate_per_km) : ''),
      apw: v.apw || (bCat === 'APW' ? 'Yes' : 'No'),
      apo: v.apo || (bCat === 'APO' ? 'Yes' : 'No'),
      accountNumber: accNo,
      bankName: bName,
      bankCategory: bCat,
      ifscCode: ifsc,
      branch: br,
      driverName: dName,
      driverAddress: dAddress,
      driverContact: dContact,
      aadhar: aadharNo,
      panNumber: panNo,
      license: licNo,
      status: v.status || 'Active',
      panCardImage: panUrl || null,
      aadharFrontImage: aadharFrontUrl || null,
      aadharBackImage: aadharBackUrl || null,
      licenseImage: licenseFrontUrl || null,
      licenseBackImage: licenseBackUrl || null,
    });

    setPreviews({
      panCardImage: panUrl || null,
      aadharFrontImage: aadharFrontUrl || null,
      aadharBackImage: aadharBackUrl || null,
      licenseImage: licenseFrontUrl || null,
      licenseBackImage: licenseBackUrl || null,
    });
  }, []);

  // Load vehicle data when in edit mode
  useEffect(() => {
    if (!isOpen) return;

    if (isEditMode && vehicle) {
      // Step 1: Immediately populate with vehicle prop
      populateFormData(vehicle);

      // Step 2: Fetch full vehicle and account details asynchronously
      if (vehicle.id) {
        let isMounted = true;
        const fetchDetails = async () => {
          try {
            const [vehResp, accResp] = await Promise.allSettled([
              getVehicleById(vehicle.id),
              getAccountDetails(vehicle.id)
            ]);

            if (!isMounted) return;

            let fullVehicle = vehicle;
            let fullAccount = null;

            if (vehResp.status === 'fulfilled' && vehResp.value?.success && vehResp.value?.data) {
              fullVehicle = vehResp.value.data;
            }
            if (accResp.status === 'fulfilled' && accResp.value?.success && accResp.value?.data) {
              fullAccount = accResp.value.data;
            }

            populateFormData(fullVehicle, fullAccount);
          } catch (err) {
            console.error("Error loading complete vehicle edit details:", err);
          }
        };

        fetchDetails();
        return () => { isMounted = false; };
      }
    } else {
      setForm(INITIAL_FORM_STATE);
      setPreviews(INITIAL_PREVIEWS_STATE);
    }
  }, [isOpen, isEditMode, vehicle, populateFormData]);

  const set = (field) => (val) => {
    let finalVal = val;
    if (field === 'contactNumber' || field === 'driverContact') {
      finalVal = val.replace(/\D/g, '').slice(0, 10);
    } else if (field === 'aadhar') {
      finalVal = val.replace(/\D/g, '').slice(0, 12);
    } else if (field === 'panNumber') {
      finalVal = val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    } else if (field === 'ifscCode') {
      finalVal = val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
    } else if (field === 'seatingCapacity') {
      finalVal = val.replace(/\D/g, '');
    } else if (field === 'ratePerKm') {
      finalVal = val.replace(/[^0-9.]/g, '');
    } else if (field === 'vehicleNumber' || field === 'registrationNumber') {
      finalVal = val.toUpperCase().replace(/[^A-Z0-9]/g, '');
    } else if (field === 'vehicleType') {
      setForm(f => {
        const prevDefault = VEHICLE_SEATING_DEFAULTS[f.vehicleType];
        const newDefault = VEHICLE_SEATING_DEFAULTS[val];
        return {
          ...f,
          vehicleType: val,
          seatingCapacity: (!f.seatingCapacity || f.seatingCapacity === prevDefault) && newDefault
            ? newDefault
            : f.seatingCapacity
        };
      });
      return;
    }
    setForm(f => ({ ...f, [field]: finalVal }));
  };

  const handleFileChange = (field) => (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm(f => ({ ...f, [field]: file }));
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setPreviews(p => ({ ...p, [field]: previewUrl }));
    }
  };

  const handleSubmit = async () => {
    // Vehicle details validation
    if (!form.registrationNumber.trim()) {
      toast.warning('Please enter registration number');
      return;
    }
    if (!form.ownerName.trim()) {
      toast.warning('Please enter owner name');
      return;
    }
    if (!form.contactNumber.trim() || form.contactNumber.length !== 10) {
      toast.warning('Please enter a valid 10-digit owner contact number');
      return;
    }
    if (!form.address.trim()) {
      toast.warning('Please enter owner address');
      return;
    }
    if (!form.vehicleBrand.trim()) {
      toast.warning('Please enter vehicle brand');
      return;
    }
    if (!form.vehicleNumber.trim()) {
      toast.warning('Please enter vehicle number');
      return;
    }
    const vehicleNumberRegex = /^[A-Z0-9]+$/;
    if (!vehicleNumberRegex.test(form.vehicleNumber.trim())) {
      toast.warning('Vehicle number must contain only capital letters and numbers');
      return;
    }
    if (!form.vehicleType.trim()) {
      toast.warning('Please select vehicle type');
      return;
    }
    if (!form.model.trim()) {
      toast.warning('Please enter vehicle model');
      return;
    }
    const seating = parseInt(form.seatingCapacity, 10);
    if (isNaN(seating) || seating <= 0) {
      toast.warning('Please enter a valid seating capacity (greater than 0)');
      return;
    }

    // Rate details validation
    const rateVal = parseFloat(form.ratePerKm);
    if (isNaN(rateVal) || rateVal <= 0) {
      toast.warning('Please enter a valid rate per KM (greater than 0)');
      return;
    }

    // Account details validation
    if (!form.accountNumber.trim()) {
      toast.warning('Please enter account number');
      return;
    }
    if (!form.bankName.trim()) {
      toast.warning('Please enter bank name');
      return;
    }
    if (!form.bankCategory) {
      toast.warning('Please select bank category');
      return;
    }
    const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!form.ifscCode.trim() || form.ifscCode.length !== 11) {
      toast.warning('Please enter a valid 11-character IFSC code');
      return;
    }
    if (!ifscPattern.test(form.ifscCode.trim())) {
      toast.warning('IFSC code format is invalid (e.g. SBIN0001234)');
      return;
    }


    // Driver details validation
    if (!form.driverName.trim()) {
      toast.warning('Please enter driver name');
      return;
    }
    if (!form.driverAddress.trim()) {
      toast.warning('Please enter driver address');
      return;
    }
    if (!form.driverContact.trim() || form.driverContact.length !== 10) {
      toast.warning('Please enter a valid 10-digit driver contact number');
      return;
    }
    if (!form.aadhar.trim() || form.aadhar.length !== 12) {
      toast.warning('Please enter a valid 12-digit Aadhar number');
      return;
    }
    const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!form.panNumber.trim() || form.panNumber.length !== 10) {
      toast.warning('Please enter a valid 10-character PAN number');
      return;
    }
    if (!panPattern.test(form.panNumber.trim())) {
      toast.warning('PAN number format is invalid (e.g. ABCDE1234F)');
      return;
    }
    if (!form.license.trim()) {
      toast.warning('Please enter driver license number');
      return;
    }

    // File attachments validation (only required on new creation)
    if (!isEditMode) {
      if (!form.panCardImage) {
        toast.warning('Please upload PAN Card document (Image or PDF)');
        return;
      }
      if (!form.aadharFrontImage) {
        toast.warning('Please upload Aadhar Front document (Image or PDF)');
        return;
      }
      if (!form.aadharBackImage) {
        toast.warning('Please upload Aadhar Back document (Image or PDF)');
        return;
      }
      if (!form.licenseImage) {
        toast.warning('Please upload License Front document (Image or PDF)');
        return;
      }
      if (!form.licenseBackImage) {
        toast.warning('Please upload License Back document (Image or PDF)');
        return;
      }
    }

    try {
      setLoading(true);
      const payload = {
        registrationNumber: form.registrationNumber,
        ownerName: form.ownerName,
        ownerContactNumber: form.contactNumber,
        address: form.address,
        vehicleBrand: form.vehicleBrand,
        vehicleNumber: form.vehicleNumber,
        vehicleType: form.vehicleType,
        model: form.model,
        seatingCapacity: seating,
        ratePerKm: rateVal,
        accountNumber: form.accountNumber,
        ownerAccountNumber: form.accountNumber,
        accountHolderName: form.ownerName,
        bankName: form.bankName,
        bankCategory: form.bankCategory,
        ifscCode: form.ifscCode,
        branch: form.branch,
        apw: form.apw,
        apo: form.apo,
        status: form.status || "active",
        driverName: form.driverName,
        driverContact: form.driverContact,
        driverAddress: form.driverAddress,
        licenseNumber: form.license,
        aadharNumber: form.aadhar,
        panNumber: form.panNumber,
      };

      // Prepare FormData to send text fields and driver document files together
      const formData = new FormData();
      Object.keys(payload).forEach(key => {
        if (payload[key] !== undefined && payload[key] !== null) {
          formData.append(key, payload[key]);
        }
      });
      if (form.panCardImage instanceof File) {
        formData.append("panImage", form.panCardImage);
        formData.append("panCardImage", form.panCardImage);
      }
      if (form.aadharFrontImage instanceof File) {
        formData.append("aadharFrontImage", form.aadharFrontImage);
      }
      if (form.aadharBackImage instanceof File) {
        formData.append("aadharBackImage", form.aadharBackImage);
      }
      if (form.licenseImage instanceof File) {
        formData.append("licenseImage", form.licenseImage);
      }
      if (form.licenseBackImage instanceof File) {
        formData.append("licenseBackImage", form.licenseBackImage);
      }

      let result;
      if (isEditMode) {
        result = await updateVehicle(vehicle.id, formData);
        if (result.success) {
          if (form.accountNumber) {
            try {
              await saveAccountDetailsByVehicle(vehicle.id, {
                accountNumber: form.accountNumber,
                bankName: form.bankName,
                bankCategory: form.bankCategory,
                ifscCode: form.ifscCode,
                branch: form.branch,
                accountHolderName: form.ownerName,
              });
            } catch (accErr) {
              console.warn("Account details fallback sync:", accErr);
            }
          }
          toast.success('Vehicle updated successfully!');
          onSave && onSave(result);
          onClose();
        } else {
          toast.error(result.message || 'Failed to update vehicle');
        }
      } else {
        result = await addVehicle(formData);
        if (result.success) {
          // Sync account details if vehicle ID was returned
          const newVehicleId = result.data?.id || result.data?.vehicle?.id;
          if (newVehicleId && form.accountNumber) {
            try {
              await saveAccountDetailsByVehicle(newVehicleId, {
                accountNumber: form.accountNumber,
                bankName: form.bankName,
                bankCategory: form.bankCategory,
                ifscCode: form.ifscCode,
                branch: form.branch,
                accountHolderName: form.ownerName,
              });
            } catch (accErr) {
              console.warn("Account details sync on add:", accErr);
            }
          }

          // If driver was created and returned, ensure any attached documents are synced
          if (result.data?.driver?.id) {
            const docFormData = new FormData();
            if (form.panCardImage instanceof File) docFormData.append("panImage", form.panCardImage);
            if (form.aadharFrontImage instanceof File) docFormData.append("aadharFrontImage", form.aadharFrontImage);
            if (form.aadharBackImage instanceof File) docFormData.append("aadharBackImage", form.aadharBackImage);
            if (form.licenseImage instanceof File) docFormData.append("licenseImage", form.licenseImage);
            if (form.licenseBackImage instanceof File) docFormData.append("licenseBackImage", form.licenseBackImage);

            if ([...docFormData.keys()].length > 0) {
              try {
                const { updateDriver } = await import('../../api/driverApi');
                await updateDriver(result.data.driver.id, docFormData);
              } catch (docErr) {
                console.error("Error syncing driver documents:", docErr);
              }
            }
          }

          toast.success('Vehicle added successfully!');
          onSave && onSave(result);
          onClose();
        } else {
          toast.error(result.message || 'Failed to add vehicle');
        }
      }
    } catch (err) {
      toast.error('Error ' + (isEditMode ? 'updating' : 'adding') + ' vehicle: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .form-input-placeholder::placeholder {
          opacity: 0.4;
          color: #999;
        }
      `}</style>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        backgroundColor: 'white', zIndex: 2001, width: '95%', maxWidth: '1200px',
        boxShadow: '0 24px 48px rgba(0,0,0,0.2)', maxHeight: '90vh', display: 'flex', flexDirection: 'column'
      }}>

        {/* Header */}
        <div style={{ backgroundColor: '#227B48', padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: 'white' }}>
            {isEditMode ? 'Edit Vehicle' : 'Add New Vehicle'}
          </h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#F04438',
            display: 'flex', alignItems: 'center', gap: '6px', fontSize: '16px', fontWeight: 500
          }}>
            Cancel <X size={20} style={{ backgroundColor: '#F04438', color: 'white', borderRadius: '50%', padding: '2px' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '32px', overflowY: 'auto' }}>

          <SectionTitle>Vehicle Details</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '24px' }}>
            <FormInput label="Registration Number" placeholder="Registration Number" value={form.registrationNumber} onChange={set('registrationNumber')} />
            <FormInput label="Owner Name" placeholder="Contact Person Name" value={form.ownerName} onChange={set('ownerName')} />
            <FormInput label="Contact Number" placeholder="Contact Person Number" value={form.contactNumber} onChange={set('contactNumber')} />
            <FormInput label="Address" placeholder="Address" value={form.address} onChange={set('address')} />
            <FormInput label="Vehicle Brand" placeholder="Vehicle Brand" value={form.vehicleBrand} onChange={set('vehicleBrand')} />
            <FormInput label="Vehicle Number" placeholder="Vehicle Number" value={form.vehicleNumber} onChange={set('vehicleNumber')} />
            <SelectInput label="Vehicle Type" options={VEHICLE_TYPES} value={form.vehicleType} onChange={set('vehicleType')} />
            <FormInput label="Model" placeholder="Model" value={form.model} onChange={set('model')} />
            <FormInput label="Seating Capacity" placeholder="Seating Capacity" value={form.seatingCapacity} onChange={set("seatingCapacity")} />
          </div>

          <SectionTitle>Rate Details</SectionTitle>
          <div style={{ display: 'flex', gap: '24px', marginBottom: '24px' }}>
            <div style={{ width: '25%' }}>
              <FormInput label="Per KM" placeholder="₹" value={form.ratePerKm} onChange={set('ratePerKm')} />
            </div>
          </div>

          <SectionTitle>Account Details</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '24px' }}>
            <FormInput label="Account Number" placeholder="Enter account number" value={form.accountNumber} onChange={set('accountNumber')} />
            <FormInput label="Bank Name" placeholder="Enter Bank Name" value={form.bankName} onChange={set('bankName')} />
            <SelectInput
              label="Bank Category"
              options={["APW", "APO"]}
              value={form.bankCategory}
              onChange={set("bankCategory")}
            />
            <FormInput label="IFSC Code" placeholder="IFSC Code" value={form.ifscCode} onChange={set('ifscCode')} />
            <FormInput label="Branch" placeholder="Branch Name" value={form.branch} onChange={set('branch')} />
          </div>

          <SectionTitle>Driver Details</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '24px' }}>
            <FormInput label="Driver Name" placeholder="Enter Driver Name" value={form.driverName} onChange={set('driverName')} />
            <FormInput label="Address" placeholder="Address" value={form.driverAddress} onChange={set('driverAddress')} />
            <FormInput label="Contact Number" placeholder="Contact Person Number" value={form.driverContact} onChange={set('driverContact')} />
            <FormInput label="Aadhar" placeholder="Enter Aadhar Number" value={form.aadhar} onChange={set('aadhar')} />
            <FormInput label="PAN Number" placeholder="Enter pan card Number" value={form.panNumber} onChange={set('panNumber')} />
            <FormInput label="License" placeholder="Enter License number" value={form.license} onChange={set('license')} />
          </div>

          <SectionTitle>Driver Documents</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '24px' }}>
            <FileUpload
              label="PAN Card"
              value={form.panCardImage}
              preview={previews.panCardImage}
              onChange={handleFileChange('panCardImage')}
            />
            <FileUpload
              label="Aadhar Front"
              value={form.aadharFrontImage}
              preview={previews.aadharFrontImage}
              onChange={handleFileChange('aadharFrontImage')}
            />
            <FileUpload
              label="Aadhar Back"
              value={form.aadharBackImage}
              preview={previews.aadharBackImage}
              onChange={handleFileChange('aadharBackImage')}
            />
            <FileUpload
              label="License Front"
              value={form.licenseImage}
              preview={previews.licenseImage}
              onChange={handleFileChange('licenseImage')}
            />
            <FileUpload
              label="License Back"
              value={form.licenseBackImage}
              preview={previews.licenseBackImage}
              onChange={handleFileChange('licenseBackImage')}
            />
          </div>

        </div>

        {/* Footer */}
        <div style={{ padding: '24px 32px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              backgroundColor: loading ? '#9CA3AF' : '#20B967',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '14px 32px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update & Save' : 'Create & Save')}
          </button>
        </div>

      </div>

    </>
  );
};

export default AddVehicleModal;

